import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';
import { useCallback, useState } from 'react';

import useCreateClientPortalPreview from '@/api/mutations/project/useCreateClientPortalPreview';
import useGetProjectByIdOverview from '@/api/queries/project/useGetProjectByIdOverview';
import { usePermissions } from '@/hooks/usePermissions';

/**
 * Opening the client portal as a Project's Customer, without the trigger that
 * starts it — so a surface can offer the action in whatever shape suits it.
 *
 * @param projectId the Project's `publicId`
 */
export const useClientPortalPreview = (projectId: string) => {
  const { userHasRequiredPermission } = usePermissions();
  const { project } = useGetProjectByIdOverview(projectId);
  const { mutate: createClientPortalPreview, isPending } =
    useCreateClientPortalPreview();

  const [blockedPreviewUrl, setBlockedPreviewUrl] = useState<string | null>(
    null
  );
  const closeBlockedPreview = useCallback(() => setBlockedPreviewUrl(null), []);

  const openPreview = () => {
    // Opened before the link exists, because a tab opened later is not a
    // response to the click and the browser blocks it. The link is single-use
    // and lives two minutes, so it is followed immediately.
    //
    // `noopener` cannot be passed here — it makes window.open return null, and
    // the handle is the whole point — so the reference is severed by hand once
    // the tab has somewhere to go.
    const previewTab = window.open('', '_blank');

    createClientPortalPreview(projectId, {
      onSuccess: ({ createClientPortalPreview: preview }) => {
        // A blocked tab comes back null. The link is already minted and good
        // for two minutes, so it is handed to the user rather than discarded.
        if (!previewTab) {
          setBlockedPreviewUrl(preview.url);
          return;
        }
        previewTab.opener = null;
        previewTab.location.href = preview.url;
      },
      onError: () => previewTab?.close(),
    });
  };

  const availability = project?.isClientPortalPreviewable;

  return {
    /**
     * Whether to offer the action at all. False for a user without the
     * permission, who has nothing to act on, and while the Project loads.
     */
    canPreview:
      userHasRequiredPermission([DealsPermissions.PREVIEW_CLIENT_PORTAL]) &&
      !!project,
    openPreview,
    isPending,
    /** Reasons the Project cannot be previewed; empty when it can. */
    unavailableReasons: availability?.result
      ? []
      : (availability?.reasons ?? []),
    /** True while the action should be blocked, whether by state or by a call in flight. */
    isBlocked: !availability?.result || isPending,
    /** Set when the browser refused the tab, so the link can be offered by hand. */
    blockedPreviewUrl,
    closeBlockedPreview,
  };
};

export default useClientPortalPreview;
